// Shared AI credits, the app side: wording for a share and how choosing one
// changes the AI settings. The rules that matter (cap, expiry, models) are
// enforced by the API; nothing here is trusted for that.

import { LLM_PROVIDERS, getDefaultModel } from "../config/llmProviders";
import { getProviderApiKey } from "./llmQuickSwitch";
import { formatAmount } from "./money";

const DAY_MS = 86_400_000;

/**
 * One of a share's amounts, in the share's own currency: an A$ cap is shown
 * and enforced in A$, a US$ cap in US$, whatever the viewer's setting.
 */
export const shareMoney = (share, amount) => formatAmount(Number(amount) || 0, share?.currency || "USD");

const formatDay = (iso) =>
  new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });

export const firstName = (name) => (name || "").trim().split(/\s+/)[0] || "Your mate";

/** "Mum's OpenRouter". */
export const shareTitle = (share) =>
  `${firstName(share?.owner?.name || share?.ownerName)}'s ${share?.providerLabel || share?.provider}`;

/** Spend against the cap: "A$1.20 of A$5.00 used", or "A$1.20 used, no limit". */
export const describeSpend = (share) =>
  share.limitAmount == null
    ? `${shareMoney(share, share.spent)} used, no limit`
    : `${shareMoney(share, share.spent)} of ${shareMoney(share, share.limitAmount)} used`;

/** What's left, for the borrower: "A$3.80 left" or "No limit". */
export const describeLeft = (share) =>
  share.limitAmount == null ? "No limit" : `${shareMoney(share, share.remaining)} left`;

export const describeExpiry = (share) =>
  share.expiresAt ? `until ${formatDay(share.expiresAt)}` : "no end date";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** Today's date as "YYYY-MM-DD" on this device. */
export const todayKey = (now = new Date()) =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;

/** A "YYYY-MM-DD" day as "Today", "Yesterday" or "7 Oct". */
export const dayName = (key, now = new Date()) => {
  if (!key) return "";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (key === todayKey(now)) return "Today";
  if (key === todayKey(yesterday)) return "Yesterday";
  const [, m, d] = key.split("-").map(Number);
  return `${d} ${MONTHS[m - 1]}`;
};

/** "Last used today", or "Not used yet". Owners only ever get the day. */
export const describeLastUsed = (share, now = new Date()) => {
  const key = share.lastUsedAt ? todayKey(new Date(share.lastUsedAt)) : share.lastUsedOn;
  if (!key) return "Not used yet";
  const name = dayName(key, now);
  return `Last used ${/^\d/.test(name) ? name : name.toLowerCase()}`;
};

/** Whole days from today until an ISO date (negative once past). */
export const daysUntil = (iso, now = new Date()) => {
  const end = new Date(iso);
  const a = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const b = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  return Math.round((b - a) / DAY_MS);
};

/**
 * The one tag a live share shows, or null: used up beats low beats ending
 * soon, so a row never carries two. `tone` picks the status colour, and the
 * word always rides with it.
 */
export const shareTag = (share, now = new Date()) => {
  if (share.limitAmount != null && share.remaining <= 0) return { label: "Used up", tone: "danger" };
  if (share.limitAmount != null && share.spent >= share.limitAmount * 0.8) return { label: "Low", tone: "warning" };
  if (share.expiresAt) {
    const days = daysUntil(share.expiresAt, now);
    if (days <= 3) return { label: days <= 0 ? "Ends today" : days === 1 ? "Ends tomorrow" : `Ends in ${days} days`, tone: "warning" };
  }
  return null;
};

/** How full the progress bar is (0–100) and its tone, or null with no cap. */
export const shareMeter = (share) => {
  if (share.limitAmount == null) return null;
  const pct = Math.min(100, Math.max(0, (share.spent / share.limitAmount) * 100));
  return { pct, tone: share.remaining <= 0 ? "danger" : pct >= 80 ? "warning" : "normal" };
};

/** Activity rows grouped under "Today", "Yesterday", "7 Oct", newest first. */
export const groupByDay = (items, keyOf, now = new Date()) => {
  const groups = [];
  for (const item of items) {
    const key = keyOf(item);
    let group = groups[groups.length - 1];
    if (!group || group.key !== key) {
      group = { key, label: dayName(key, now), items: [] };
      groups.push(group);
    }
    group.items.push(item);
  }
  return groups;
};

/** "10:32 am" on this device. */
export const formatTime = (iso) =>
  new Date(iso).toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });

/** One call's time for the activity list: "9 Oct, 4:12 pm". */
export const formatWhen = (iso) =>
  new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });

/** "anthropic/claude-haiku-5.5" reads as "claude-haiku-5.5" in a narrow row. */
export const shortModel = (model) => String(model || "").split("/").pop();

/** Why an ended share ended, for the muted rows. */
export const describeEnded = (share) => {
  switch (share.status) {
    case "revoked":
      return `Stopped ${formatDay(share.revokedAt)}`;
    case "expired":
      return `Ended ${formatDay(share.expiresAt)}`;
    case "spent":
      return "Limit reached";
    default:
      return "";
  }
};

/** The models a share covers: its own list, or every model the provider offers. */
export const shareModels = (share) =>
  share?.allowedModels?.length ? share.allowedModels : LLM_PROVIDERS[share?.provider]?.models || [];

/** What llmSettings keeps about the share in use: enough to route and label. */
export const shareSnapshot = (share) => ({
  id: share.id,
  ownerName: share.owner?.name || share.ownerName || null,
  provider: share.provider,
  providerLabel: share.providerLabel,
  allowedModels: share.allowedModels || null,
});

/**
 * Settings that use a mate's share. Keeps the current model when the share
 * covers it, otherwise the provider's default if covered, otherwise the first
 * covered model. The user's own key for that provider is left in place, so
 * switching back later needs nothing re-entered.
 */
export const settingsForShare = (settings, share) => {
  const models = shareModels(share);
  const preferred = [settings.provider === share.provider ? settings.model : null, getDefaultModel(share.provider)];
  const model = preferred.find((m) => m && models.includes(m)) || models[0] || "";
  return {
    ...settings,
    provider: share.provider,
    model,
    apiKey: getProviderApiKey(settings, share.provider),
    creditShare: shareSnapshot(share),
  };
};

/** True when these settings run on a mate's credits. */
export const isOnShare = (settings) => Boolean(settings?.creditShare?.id);

// Spending limit and end date choices, in the order the sheets show them.
const LIMIT_STEPS = { AUD: [5, 10, 20, 30], USD: [2, 5, 10, 20] };

/** Spending limit choices in a currency: round amounts in that currency. */
export const limitChoicesFor = (currency) => [
  ...(LIMIT_STEPS[currency] || LIMIT_STEPS.USD).map((n) => ({
    value: String(n),
    label: formatAmount(n, currency).replace(".00", ""),
  })),
  { value: "none", label: "No limit" },
];

export const EXPIRY_CHOICES = [
  { value: "week", label: "1 week" },
  { value: "month", label: "1 month" },
  { value: "3months", label: "3 months" },
  { value: "none", label: "No end date" },
];

/** An end date from a choice, counted from `now`. */
export const expiryFromChoice = (choice, now = new Date()) => {
  switch (choice) {
    case "week":
      return new Date(now.getTime() + 7 * DAY_MS).toISOString();
    case "month": {
      const at = new Date(now);
      at.setMonth(at.getMonth() + 1);
      return at.toISOString();
    }
    case "3months": {
      const at = new Date(now);
      at.setMonth(at.getMonth() + 3);
      return at.toISOString();
    }
    default:
      return null;
  }
};

export const limitFromChoice = (choice) => (choice === "none" || choice == null ? null : Number(choice));

// ── Credit invites ──────────────────────────────────────
// A link or QR code that gives whoever claims it a small allowance on your
// key. The API holds the rules (amount, people, days); these are the choices
// the sheet offers and how an invite is worded.

const INVITE_AMOUNT_STEPS = { AUD: [1, 2, 5, 10], USD: [1, 2, 5] };

/** "Each person gets" choices in a currency. */
export const inviteAmountChoices = (currency) =>
  (INVITE_AMOUNT_STEPS[currency] || INVITE_AMOUNT_STEPS.USD).map((n) => ({
    value: String(n),
    label: formatAmount(n, currency).replace(".00", ""),
  }));

export const INVITE_DAYS_CHOICES = [
  { value: "7", label: "1 week" },
  { value: "14", label: "2 weeks" },
  { value: "30", label: "1 month" },
];

export const INVITE_PEOPLE_CHOICES = [1, 3, 5, 10].map((n) => ({
  value: String(n),
  label: n === 1 ? "1 person" : `${n} people`,
}));

/** The most a link can cost: "Up to A$10 if everyone uses it all". */
export const describeInviteTotal = ({ amount, people, currency }) => {
  const total = Number(amount) * Number(people);
  const money = formatAmount(total, currency).replace(".00", "");
  return Number(people) === 1 ? `Up to ${money} if they use it all` : `Up to ${money} if everyone uses it all`;
};

/** One line for an invite link: "A$2 each · 2 of 5 claimed". */
export const describeCreditInvite = (invite) => {
  const each = formatAmount(Number(invite.credit_amount), invite.credit_currency || "USD").replace(".00", "");
  return `${each} each · ${invite.use_count || 0} of ${invite.max_uses} claimed`;
};

/**
 * Should claiming credits switch the AI engine to them? Yes, unless the person
 * already runs on a key of their own: someone set up with their own key chose
 * that, and the credits wait in Shared credits for when they want them.
 */
export const shouldUseClaimedShare = (settings) => {
  if (!settings || isOnShare(settings)) return true;
  const config = LLM_PROVIDERS[settings.provider];
  return !(config?.apiKeyRequired && settings.apiKey);
};

/** The mate may ask for more unless the owner stopped sharing. */
export const canAskForMore = (share) => Boolean(share) && share.status !== "revoked";
